"use client";
import { useCallback } from "react";
import { useAuth } from "@clerk/nextjs";
import { useNativeSignIn } from "@/hooks/useNativeSignIn";
import { useFiltersStore } from "@/stores/filtersStore";
import { useTokenBudgetStore } from "@/stores/tokenBudgetStore";
import { useUiStore } from "@/stores/uiStore";
import { TOKEN_UI_ENABLED } from "@/lib/shared/featureFlags";

/**
 * Shared KURBO action handler used by all KURBO interaction points.
 *
 * Three-way branch:
 *  1. Not signed in       → open Clerk sign-in modal
 *  2. Signed in, no tokens → open the no-tokens nag modal
 *  3. Signed in, has tokens → enable KURBO mode
 */
export function useKurboAction() {
  const { isSignedIn } = useAuth();
  const { openSignIn } = useNativeSignIn();
  const setKurboMode = useFiltersStore((s) => s.actions.setKurboMode);
  const tokensRemaining = useTokenBudgetStore((s) => s.tokensRemaining);
  const tokensInitialized = useTokenBudgetStore((s) => s.initialized);
  const openKurboNoTokensNag = useUiStore((s) => s.actions.openKurboNoTokensNag);

  const handleKurboAction = useCallback(() => {
    if (!isSignedIn) {
      openSignIn();
      return;
    }
    if (TOKEN_UI_ENABLED && tokensInitialized && tokensRemaining === 0) {
      openKurboNoTokensNag();
      return;
    }
    setKurboMode(true);
  }, [
    isSignedIn,
    openSignIn,
    tokensInitialized,
    tokensRemaining,
    openKurboNoTokensNag,
    setKurboMode,
  ]);

  return { handleKurboAction };
}
